export interface MessageSummary {
  id: number;
  subject: string;
  from_name: string;
  from_email: string;
  date: string;
  seen: number;
  flagged: number;
  answered: number;
  snippet: string;
  folder_id: number;
  account_id: number;
  attachment_count: number;
  category: string | null;
  priority: string | null;
  spam_score: number | null;
  needs_reply: number | null;
  summary: string | null;
  reason: string | null;
  pending_actions: number;
  draft_id: number | null;
  /** Pfad des Ordners — für die Markierung von Mails aus Unterordnern. */
  folder_path?: string;
  /** Nur mit „Unterhaltungen": Nachrichten im ganzen Faden, und ob davon etwas ungelesen ist. */
  thread_anzahl?: number;
  thread_ungelesen?: number | null;
}

export interface SpamSignal { id: string; label: string; weight: number; detail?: string }

export interface SenderReputation {
  address: { hamCount: number; spamCount: number; replied: boolean; manual: string | null } | null;
  domain: { hamCount: number; spamCount: number; replied: boolean; manual: string | null } | null;
}

export interface MessageDetail extends MessageSummary {
  heuristic: {
    score: number | null;
    signals: SpamSignal[];
    spf: string | null;
    dkim: string | null;
    dmarc: string | null;
  };
  senderReputation: SenderReputation;
  body_text: string | null;
  body_html: string | null;
  folder_path: string;
  to: string[];
  cc: string[];
  /** inline: per cid: im HTML eingebettet (Signatur, Logo) — steht schon in der Mail. */
  attachments: { filename: string; contentType: string; size: number; inline?: boolean }[];
  heuristic_score?: number | null;
  entities: Record<string, string>;
  model: string | null;
  actions: ActionRow[];
  thread: { id: number; subject: string; from_name: string; from_email: string; date: string; seen: number }[];
}

export interface ActionRow {
  id: number;
  message_id: number;
  type: string;
  payload_json: string;
  source: string;
  status: string;
  confidence: number;
  error: string | null;
  created_at: string;
  rule_name?: string | null;
  subject?: string;
  from_email?: string;
}

export interface Folder {
  id: number;
  account_id: number;
  path: string;
  name: string;
  special_use: string | null;
  delimiter?: string;
  total: number;
  unseen: number;
}

export interface AnwendenOptionen { ruleIds?: number[]; folderIds?: number[]; belege?: boolean }

export interface AnwendenTreffer { id: number; subject: string; from: string; date: string; folder: string; aenderungen: string[] }

export interface AnwendenVorschau {
  geprueft: number;
  betroffen: number;
  gruppen: { ruleId: number; name: string; anzahl: number; treffer: AnwendenTreffer[] }[];
  zaehler: { verschieben: number; kategorie: number; gelesen: number; markieren: number; spam: number; belege: number };
}

export interface AnwendenStatus {
  laeuft: boolean; gesamt: number; erledigt: number; verschoben: number; belege: number;
  fehler: number; letzterFehler: string | null; gestartet: string | null; beendet: string | null;
}

export type LeerenUrteil = "spam" | "unsicher" | "erwuenscht";

export interface LeerenEintrag {
  id: number;
  subject: string;
  fromName: string;
  fromEmail: string;
  date: string | null;
  category: string | null;
  /** Einschätzung des Sprachmodells beim Eintreffen, 0–1. */
  spamScore: number | null;
  /** Frisch berechnete Punktzahl der Heuristik. */
  heuristicScore: number | null;
  verdict: "ham" | "unsure" | "spam" | null;
  urteil: LeerenUrteil;
  gruende: string[];
  signale: { label: string; weight: number }[];
}

export interface LeerenPruefung {
  folder: { id: number; path: string; specialUse: string | null };
  /** Anzahl auf dem Server; kann größer sein als der lokale Spiegel. */
  aufServer: number | null;
  zusammenfassung: Record<LeerenUrteil, number>;
  eintraege: LeerenEintrag[];
}

export interface UnterhaltungsNachricht {
  id: number;
  subject: string;
  from_name: string;
  from_email: string;
  to: string[];
  date: string;
  seen: number;
  ordner: string;
  anhaenge: number;
  /** Von dir — aus „Gesendet" oder von einer eigenen Adresse. */
  eigene: boolean;
  /** Nur der neue Text, ohne Zitat. */
  text: string;
  body_html: string | null;
}

export interface Account { id: number; name: string; email: string; active: boolean; hasSmtp: boolean }

export interface Condition { field: string; op: string; value: string | string[] | boolean; caseSensitive?: boolean }
export interface Rule {
  id?: number;
  name: string;
  enabled: boolean;
  priority: number;
  match: { all?: Condition[]; any?: Condition[]; none?: Condition[] };
  actions: { type: string; [k: string]: unknown }[];
  stopProcessing: boolean;
  hitCount?: number;
  lastHitAt?: string | null;
}

export interface Kontakt {
  email: string;
  name: string;
  anzahl: number;
  geschrieben: boolean;
}

export interface DraftUpload {
  id: number;
  filename: string;
  contentType: string;
  size: number;
}

export type ComposeKind = "antwort" | "allen" | "weiterleiten" | "neu";

export interface Draft {
  id: number;
  account_id: number;
  reply_to_id: number | null;
  to_json: string;
  cc_json: string;
  bcc_json: string;
  attachments_json: string;
  kind: ComposeKind;
  subject: string;
  body: string;
  source: string;
  model: string | null;
  created_at: string;
  reply_subject: string | null;
  reply_from: string | null;
}

export interface DocumentRow {
  id: number;
  vendor_id: number | null;
  vendor_name: string;
  message_id: number | null;
  source: string;
  doc_type: string;
  invoice_number: string | null;
  issue_date: string | null;
  period: string | null;
  currency: string;
  net: number | null;
  vat_rate: number | null;
  vat_amount: number | null;
  gross: number | null;
  tax_treatment: string | null;
  vat_id: string | null;
  file_name: string;
  file_size: number;
  status: string;
  problems: string;
  mail_subject: string | null;
  /** Die Herkunftsmail, auch nach dem Verschieben über die Message-ID gefunden. */
  mail_id: number | null;
  mail_from: string | null;
  mail_date: string | null;
  duplicate_of: number | null;
  /** Rechnungsempfänger laut Dokument, falls erkannt. */
  recipient?: string | null;
  created_at: string;
}

export type BelegFeld = "absender" | "domain" | "betreff";
export type BelegAktion = "ignorieren" | "unterlage" | "immer";
export interface BelegRegel { id: number; feld: BelegFeld; wert: string; aktion: BelegAktion; created_at: string }

export interface Vendor {
  id: number;
  name: string;
  legalName: string | null;
  matchDomains: string[];
  matchSubject: string | null;
  defaultTax: string;
  defaultVatRate: number | null;
  cadence: string;
  expectedDay: number | null;
  sourceType: string;
  sourceConfig: Record<string, unknown>;
  hasCredentials: boolean;
  active: boolean;
  notes: string | null;
}

export interface ChecklistEntry {
  vendorId: number;
  vendor: string;
  cadence: string;
  sourceType: string;
  expectedDay: number | null;
  state: "da" | "fehlt" | "ueberfaellig" | "nicht_erwartet";
  documentId: number | null;
  gross: number | null;
  lastSeenPeriod: string | null;
  lastRun: { status: string; finishedAt: string | null; detail: string } | null;
}

export interface VatSummary {
  period: string;
  vorsteuer: { rate: number; net: number; vat: number; count: number }[];
  reverseCharge: { net: number; vat: number; count: number };
  igErwerb: { net: number; vat: number; count: number };
  steuerfrei: { net: number; count: number };
  totals: { documents: number; gross: number; vorsteuerGesamt: number };
  problems: { id: number; vendor: string; issue: string }[];
  missing: ChecklistEntry[];
  taxLabels: Record<string, string>;
}

export interface Absence {
  id: number;
  accountId: number;
  name: string;
  kind: "zeitraum" | "wiederkehrend";
  startsOn: string | null;
  endsOn: string | null;
  weekdays: number[];
  startTime: string | null;
  endTime: string | null;
  subject: string;
  body: string;
  mode: "still" | "vorbereitend" | "antwortend";
  active: boolean;
  aktivJetzt?: boolean;
  antworten?: number;
}

export interface ReindexStatus {
  laeuft: boolean;
  scope: "regeln" | "alles" | null;
  gesamt: number;
  erledigt: number;
  perRegel: number;
  perModell: number;
  uebersprungen: number;
  fehler: number;
  letzterFehler: string | null;
  gestartet: string | null;
  beendet: string | null;
}

export interface ReindexVorschau {
  gesamt: number;
  perRegel: number;
  perModell: number;
  ausgeschlossen: number;
  tokensGeschaetzt: number;
  modellVerfuegbar: boolean;
}

export interface ReindexOptions {
  scope: "regeln" | "alles";
  nurNeue?: boolean;
  folder?: string;
}

export interface PrivacySettings {
  redaction: "aus" | "standard" | "streng";
  bodyChars: number;
  excludedFolders: string[];
  styleSamples: boolean;
  logos: boolean;
}

export interface PromptPreview {
  ausgeschlossen: boolean;
  ordner: string;
  stufe: string;
  zeichenGrenze: number;
  text: string | null;
  zeichen: number;
  geschwaerzt: Record<string, number>;
  beschreibung: string;
  bereitsGesendet: { model: string; tokens: number; geschwaerzt: Record<string, number> } | null;
}

export interface ProviderInfo {
  id: string;
  name: string;
  protocol: "anthropic" | "openai";
  hosting: string;
  dsgvo: "eu" | "av-vertrag" | "selbst";
  note: string;
  needsBaseUrl: boolean;
}

export interface SetupState {
  canCreateFirstUser: boolean;
  hasUser: boolean;
  users: number;
  accounts: { id: number; name: string; email: string; active: boolean }[];
  accountCount: number;
  llm: {
    configured: boolean;
    provider: string;
    protocol: "anthropic" | "openai";
    model: string;
    fastModel: string;
    region: string;
    baseUrl: string;
    /** Nur Azure: Version der Schnittstelle, gehört dort in jede Adresse. */
    apiVersion?: string;
    fromDatabase: boolean;
  };
  dryRun: boolean;
  done: boolean;
}

export interface AccountInput {
  email: string;
  name: string;
  imapHost: string;
  imapPort: number;
  imapUser: string;
  imapPass: string;
  smtpHost: string | null;
  smtpPort: number | null;
  signature: string | null;
}

export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

/** Eine Entscheidung, die der Agent vorbereitet hat — etwa einen Kundenordner anlegen. */
export interface Aufgabe {
  id: number;
  accountId: number;
  art: "kundenordner";
  messageId: number | null;
  titel: string;
  daten: {
    email: string;
    name: string;
    feld: "from" | "from_domain";
    wert: string;
    ordnerName: string;
    elternPfad: string;
    delimiter: string;
    betreff: string;
  };
  status: "offen" | "erledigt" | "abgelehnt";
  ergebnis: { ordner?: string; regelId?: number; verschoben?: number } | null;
  createdAt: string;
  decidedAt: string | null;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: "include",
    headers: init?.body ? { "Content-Type": "application/json" } : undefined,
    ...init,
  });
  if (!res.ok) {
    let message = `${res.status}`;
    try {
      message = ((await res.json()) as { error?: string }).error ?? message;
    } catch {
      /* Antwort war kein JSON */
    }
    throw new ApiError(message, res.status);
  }
  return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
}

const post = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: "POST", body: body === undefined ? undefined : JSON.stringify(body) });

export const api = {
  config: () => request<{ dryRun: boolean; model: string; provider: string }>("/api/config"),
  me: () => request<{ user: string }>("/api/auth/me"),
  login: (username: string, password: string) => post<{ ok: true }>("/api/auth/login", { username, password }),
  logout: () => post<{ ok: true }>("/api/auth/logout"),

  accounts: () => request<Account[]>("/api/accounts"),

  setupState: () => request<SetupState>("/api/setup/state"),
  account: (id: number) => request<{ id: number; name: string; email: string; signature: string | null; active: number }>(`/api/accounts/${id}`),
  updateAccount: (id: number, patch: { name?: string; signature?: string | null; active?: boolean }) =>
    request(`/api/accounts/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),

  absences: () => request<Absence[]>("/api/absences"),
  createAbsence: (a: Absence) => post<{ id: number }>("/api/absences", a),
  updateAbsence: (id: number, a: Absence) => request(`/api/absences/${id}`, { method: "PUT", body: JSON.stringify(a) }),
  deleteAbsence: (id: number) => request(`/api/absences/${id}`, { method: "DELETE" }),
  absenceReplies: (id: number) => request<{ recipient: string; sent_at: string }[]>(`/api/absences/${id}/antworten`),

  reindexVorschau: (o: ReindexOptions) => post<ReindexVorschau>("/api/reindex/vorschau", o),
  reindexStatus: () => request<ReindexStatus>("/api/reindex"),
  reindexStart: (o: ReindexOptions) => post<ReindexStatus>("/api/reindex", o),
  reindexStop: () => post<{ ok: true }>("/api/reindex/stop"),

  privacy: () => request<PrivacySettings>("/api/settings/privacy"),
  savePrivacy: (patch: Partial<PrivacySettings>) => post<PrivacySettings>("/api/settings/privacy", patch),
  messagePrompt: (id: number) => request<PromptPreview>(`/api/messages/${id}/prompt`),

  pushKey: () => request<{ publicKey: string; subscriptions: number }>("/api/push/key"),
  pushSubscribe: (sub: { endpoint: string; keys: { p256dh: string; auth: string }; label?: string }) =>
    post<{ ok: true; subscriptions: number }>("/api/push/subscribe", sub),
  pushUnsubscribe: (endpoint: string) => post<{ ok: true }>("/api/push/unsubscribe", { endpoint }),
  pushTest: () => post<{ sent: number; removed: number }>("/api/push/test"),
  createFirstUser: (username: string, password: string) => post<{ ok: true }>("/api/setup/user", { username, password, token: einrichtungsToken() }),
  providers: () => request<{ providers: ProviderInfo[] }>("/api/setup/providers"),
  testLlmKey: (v: { provider: string; apiKey: string; region?: string; baseUrl?: string; apiVersion?: string }) =>
    post<{ ok: true; models: { id: string; display_name?: string }[]; hinweis?: string }>("/api/setup/llm/models", v),
  saveLlm: (v: {
    provider: string; apiKey: string; region?: string; model: string;
    fastModel: string | null; baseUrl?: string | null; apiVersion?: string;
  }) => post<{ ok: true }>("/api/setup/llm", v),
  testAccount: (v: AccountInput) =>
    post<{
      imap: { ok: boolean; error: string | null; folders: { path: string; specialUse: string | null }[] };
      smtp: { ok: boolean; error: string | null; skipped: boolean };
    }>("/api/accounts/test", v),
  createAccount: (v: AccountInput) => post<{ id: number; folders: number }>("/api/accounts", v),
  deleteAccount: (id: number) => request(`/api/accounts/${id}`, { method: "DELETE" }),
  completeSetup: (dryRun: boolean) => post<{ ok: true; dryRun: boolean }>("/api/setup/complete", { dryRun }),
  setDryRun: (dryRun: boolean) => post<{ ok: true; dryRun: boolean }>("/api/settings/dry-run", { dryRun }),
  folders: () => request<Folder[]>("/api/folders"),

  messages: (params: Record<string, string | number | boolean | undefined>) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== "") qs.set(k, String(v));
    return request<MessageSummary[]>(`/api/messages?${qs}`);
  },
  message: (id: number) => request<MessageDetail>(`/api/messages/${id}`),
  messageAction: (id: number, type: string, folder?: string) => post(`/api/messages/${id}/action`, { type, folder }),
  leerenPruefung: (folderId: number) => post<LeerenPruefung>(`/api/folders/${folderId}/leeren/pruefung`, {}),
  leeren: (folderId: number, geprueft: number[], retten: number[]) =>
    post<{ geloescht: number; gerettet: number; uebersprungen: number }>(`/api/folders/${folderId}/leeren`, { geprueft, retten }),
  bulkAction: (ids: number[], action: "move" | "trash" | "markRead" | "markUnread" | "flag" | "unflag" | "spam", folder?: string) =>
    post<{ erledigt: number; fehlgeschlagen: number; fehler: string[] }>("/api/messages/bulk", { ids, action, folder }),
  /** Braucht keine Antwort — verlässt „Zu beantworten". */
  erledigt: (ids: number[], erledigt = true) => post<{ ok: boolean }>("/api/messages/erledigt", { ids, erledigt }),
  correct: (id: number, category: string, note?: string) => post(`/api/messages/${id}/correct`, { category, note }),
  markSender: (id: number, manual: "ham" | "spam" | null) => post(`/api/messages/${id}/sender`, { manual }),

  pendingActions: () => request<ActionRow[]>("/api/actions?status=pending"),
  aufgaben: (status: Aufgabe["status"] | "alle" = "offen") => request<Aufgabe[]>(`/api/aufgaben?status=${status}`),
  aufgabenAnzahl: () => request<{ offen: number }>("/api/aufgaben/anzahl"),
  ungelesen: () => request<{ anzahl: number }>("/api/ungelesen"),
  aufgabeAnnehmen: (id: number, ordnerName?: string) =>
    post<{ ordner: string; regelId: number; verschoben: number }>(`/api/aufgaben/${id}/annehmen`, { ordnerName }),
  aufgabeAblehnen: (id: number) => post<{ ok: boolean }>(`/api/aufgaben/${id}/ablehnen`),
  approve: (id: number) => post(`/api/actions/${id}/approve`),
  reject: (id: number) => post(`/api/actions/${id}/reject`),
  undo: (id: number) => post(`/api/actions/${id}/undo`),

  kontakte: (q: string) => request<Kontakt[]>(`/api/kontakte?q=${encodeURIComponent(q)}`),
  absender: (q = "") => request<Kontakt[]>(`/api/kontakte?modus=absender&q=${encodeURIComponent(q)}`),

  uploads: (draftId: number) => request<DraftUpload[]>(`/api/drafts/${draftId}/uploads`),
  addUpload: (draftId: number, filename: string, contentBase64: string, contentType?: string) =>
    post<DraftUpload>(`/api/drafts/${draftId}/uploads`, { filename, contentBase64, contentType }),
  removeUpload: (draftId: number, id: number) =>
    request(`/api/drafts/${draftId}/uploads/${id}`, { method: "DELETE" }),

  compose: (messageId: number, kind: ComposeKind) =>
    post<{ id: number }>(`/api/messages/${messageId}/compose`, { kind }),
  generateDraft: (messageId: number, instruction?: string, opts: { anhaengeLesen?: boolean } = {}) =>
    post<{
      id: number; body: string; model: string;
      /** Nur mit anhaengeLesen: was das Modell zu sehen bekam. */
      gelesen: { bilder: string[]; pdfs: string[]; uebersprungen: string[] } | null;
    }>(`/api/messages/${messageId}/draft`, { instruction, anhaengeLesen: opts.anhaengeLesen ?? false }),
  /** Formuliert den offenen Entwurf aus Stichpunkten und/oder dem, was schon dasteht. */
  formulieren: (
    draftId: number,
    eingabe: { stichpunkte: string; body: string; subject: string; to: string[]; anhaengeLesen?: boolean; modus?: "formulieren" | "anpassen" },
  ) =>
    post<{
      body: string;
      /** Nur bei neuen Mails ohne Betreff: ein Vorschlag. */
      subject: string | null;
      model: string;
      gelesen: { bilder: string[]; pdfs: string[]; uebersprungen: string[] } | null;
    }>(`/api/drafts/${draftId}/formulieren`, eingabe),
  drafts: () => request<Draft[]>("/api/drafts"),
  unterhaltung: (messageId: number) => request<{ nachrichten: UnterhaltungsNachricht[] }>(`/api/messages/${messageId}/unterhaltung`),
  neuerEntwurf: (to: string[] = []) => post<{ id: number }>("/api/drafts", { to }),
  updateDraft: (
    id: number,
    patch: {
      subject?: string; body?: string; to?: string[]; cc?: string[]; bcc?: string[];
      attachments?: { messageId: number; index: number; filename: string; size: number }[];
      accountId?: number;
    },
  ) =>
    request(`/api/drafts/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
  deleteDraft: (id: number) => request(`/api/drafts/${id}`, { method: "DELETE" }),
  sendDraft: (id: number) => post<{ ok: true }>(`/api/drafts/${id}/send`),

  rules: () => request<Rule[]>("/api/rules"),
  createRule: (rule: Rule) => post<{ id: number }>("/api/rules", rule),
  updateRule: (id: number, rule: Rule) => request(`/api/rules/${id}`, { method: "PUT", body: JSON.stringify(rule) }),
  deleteRule: (id: number) => request(`/api/rules/${id}`, { method: "DELETE" }),
  testRule: (rule: Rule) =>
    post<{ tested: number; matched: number; hits: { id: number; subject: string; from: string; date: string }[] }>(
      "/api/rules/test",
      { rule },
    ),
  anwendenVorschau: (o: AnwendenOptionen) => post<AnwendenVorschau>("/api/rules/anwenden/vorschau", o),
  anwendenStarten: (ids: number[], o: AnwendenOptionen) => post<{ ok: boolean; gesamt: number }>("/api/rules/anwenden", { ...o, ids }),
  anwendenStatus: () => request<AnwendenStatus>("/api/rules/anwenden"),
  anwendenStop: () => post<{ ok: boolean }>("/api/rules/anwenden/stop", {}),
  ordnerInhaltVerschieben: (id: number, ziel: string) => post<{ anzahl: number }>(`/api/folders/${id}/inhalt-verschieben`, { ziel }),
  ordnerUmbenennen: (id: number, name: string) => post<{ path: string }>(`/api/folders/${id}/umbenennen`, { name }),
  ordnerLoeschen: (id: number, inhaltNach?: string) =>
    request<{ verschoben: number }>(`/api/folders/${id}${inhaltNach ? `?inhaltNach=${encodeURIComponent(inhaltNach)}` : ""}`, { method: "DELETE" }),
  ordnerEinhaengen: (id: number, elternId: number | null) => post<{ path: string }>(`/api/folders/${id}/einhaengen`, { elternId }),
  /** Ohne `accountId` im ersten Postfach — aus dem Ordnermenü immer mit dem Konto des Elternordners. */
  ordnerAnlegen: (teile: string[], accountId?: number) => post<{ path: string }>("/api/folders", { teile, accountId }),
  suggestRule: (messageId: number) => request<Rule>(`/api/rules/suggest/${messageId}`),

  vendors: () => request<Vendor[]>("/api/vendors"),
  vendorTemplates: () =>
    request<{ recipes: Record<string, unknown>; taxLabels: Record<string, string> }>("/api/vendors/templates"),
  createVendor: (v: Partial<Vendor>) => post<{ id: number }>("/api/vendors", v),
  updateVendor: (id: number, v: Partial<Vendor>) => request(`/api/vendors/${id}`, { method: "PUT", body: JSON.stringify(v) }),
  deleteVendor: (id: number) => request(`/api/vendors/${id}`, { method: "DELETE" }),
  setCredentials: (id: number, c: { benutzer: string; passwort: string } | null) =>
    request(`/api/vendors/${id}/credentials`, { method: "PUT", body: JSON.stringify(c) }),
  testVendor: (id: number) =>
    post<{ found: number; stored: number; errors: string[]; screenshot: string | null }>(`/api/vendors/${id}/test`),
  collectVendor: (id: number) =>
    post<{ found: number; stored: number; errors: string[]; screenshot: string | null }>(`/api/vendors/${id}/collect`),

  documents: (params: { period?: string; vendorId?: number; status?: string } = {}) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v !== undefined) qs.set(k, String(v));
    return request<DocumentRow[]>(`/api/documents?${qs}`);
  },
  periods: () => request<{ period: string; n: number; gross: number; problems: number }[]>("/api/documents/periods"),
  updateDocument: (id: number, patch: Record<string, unknown>) =>
    request(`/api/documents/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
  deleteDocument: (id: number) => request(`/api/documents/${id}`, { method: "DELETE" }),
  belegRegeln: () => request<{ regeln: BelegRegel[]; eigeneNamen: string[]; eigeneNamenGesetzt: boolean }>("/api/belege/regeln"),
  belegRegelAnlegen: (feld: BelegFeld, wert: string, aktion: BelegAktion) =>
    post<{ regel: BelegRegel; angewendet: number }>("/api/belege/regeln", { feld, wert, aktion }),
  belegRegelLoeschen: (id: number) => request(`/api/belege/regeln/${id}`, { method: "DELETE" }),
  eigeneNamenSetzen: (namen: string[]) =>
    request<{ eigeneNamen: string[] }>("/api/belege/eigene-namen", { method: "PUT", body: JSON.stringify({ namen }), headers: { "Content-Type": "application/json" } }),
  uploadDocument: (fileName: string, contentBase64: string, vendorId?: number | null) =>
    post<{ id: number; duplicate: boolean }>("/api/documents/upload", { fileName, contentBase64, vendorId }),
  collectFromMessage: (messageId: number) => post<unknown>(`/api/messages/${messageId}/collect`),

  ustva: (period: string) => request<VatSummary>(`/api/belege/ustva?period=${period}`),
  checklistFor: (period: string) =>
    request<{ period: string; entries: ChecklistEntry[] }>(`/api/belege/checklist?period=${period}`),

  stats: () =>
    request<{
      byCategory: { category: string; n: number }[];
      actions: { status: string; n: number }[];
      tokens: { tokens_in: number; tokens_out: number; calls: number };
      corrections: { n: number };
      topRules: { name: string; hit_count: number; last_hit_at: string | null }[];
      /** Richtwert je tatsächlich genutztem Modell; fehlt bei älteren Servern. */
      kosten?: { eur: number; unbekannt: string[]; jeModell: { model: string; tokensIn: number; tokensOut: number; eur: number | null }[] };
    }>("/api/stats"),
  audit: (limit = 120) =>
    request<{ id: number; ts: string; actor: string; action: string; message_id: number | null; detail: string; subject: string | null }[]>(
      `/api/audit?limit=${limit}`,
    ),
};

export const CATEGORIES = [
  "beleg", "rechnung", "kunde", "lieferant", "newsletter",
  "benachrichtigung", "termin", "bewerbung", "privat", "spam", "sonstiges",
] as const;

export function categoryColor(category: string | null | undefined): string {
  return category ? `var(--cat-${category}, var(--ink-3))` : "transparent";
}

export function formatDate(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
  if (d.getFullYear() === now.getFullYear()) return d.toLocaleDateString("de-DE", { day: "2-digit", month: "short" });
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit" });
}

export function euro(n: number | null | undefined): string {
  if (n === null || n === undefined) return "—";
  return n.toLocaleString("de-DE", { style: "currency", currency: "EUR" });
}

export function currentPeriod(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export function periodLabel(period: string): string {
  const [y, m] = period.split("-");
  const months = ["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"];
  return `${months[Number(m) - 1] ?? m} ${y}`;
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} kB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * Gehostete Instanz: Der Link aus dem Kundenkonto trägt ?einrichtung=…. Das
 * Token wird für die Sitzung gemerkt und aus der Adresszeile entfernt, damit
 * es nicht in Lesezeichen oder im Verlauf landet.
 */
function einrichtungsToken(): string | undefined {
  try {
    const url = new URL(window.location.href);
    const t = url.searchParams.get("einrichtung");
    if (t) {
      sessionStorage.setItem("maildeck-einrichtung", t);
      url.searchParams.delete("einrichtung");
      window.history.replaceState(window.history.state, "", url.toString());
      return t;
    }
    return sessionStorage.getItem("maildeck-einrichtung") ?? undefined;
  } catch {
    return undefined;
  }
}
if (typeof window !== "undefined") einrichtungsToken();
